import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react';

import type { NovaDimension, NovaLauncherSize } from './protocol';

interface IframeHostProps {
  iframeId: string;
  iframeRef: RefObject<HTMLIFrameElement | null>;
  embedPath?: string;
  dimension: NovaDimension;
  open: boolean;
  launcher?: NovaLauncherSize;
  launcherRight: number;
  transparentLauncher?: boolean;
  onMount(iframe: HTMLIFrameElement | null): void;
  onLoad(): void;
}

export function IframeHost({
  iframeId,
  iframeRef,
  embedPath = '/chat/embed',
  dimension,
  open,
  launcher,
  launcherRight,
  transparentLauncher,
  onMount,
  onLoad,
}: IframeHostProps) {
  const [viewport, setViewport] = useState({ width: 0, height: 0 });
  const onMountRef = useRef(onMount);
  useEffect(() => {
    onMountRef.current = onMount;
  }, [onMount]);
  const setIframeRef = useCallback(
    (iframe: HTMLIFrameElement | null) => {
      iframeRef.current = iframe;
      onMountRef.current(iframe);
    },
    [iframeRef],
  );

  useEffect(() => {
    let frame = 0;
    const update = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        setViewport((previous) => {
          const next = { width: window.innerWidth, height: window.innerHeight };
          return previous.width === next.width && previous.height === next.height ? previous : next;
        });
      });
    };
    update();
    window.addEventListener('resize', update);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', update);
    };
  }, []);

  const geometry = useMemo(() => {
    const targetWidth = dimension === 'panel-sidebar' ? 1088 : 863;
    const targetHeight = Math.max(viewport.height * 0.5, 650);
    const fullscreen = dimension === 'fullscreen' || viewport.width < targetWidth || viewport.height < targetHeight;
    return fullscreen
      ? { fullscreen, width: '100dvw', height: '100dvh', right: 0, borderRadius: 0, boxShadow: 'none' }
      : {
          fullscreen,
          width: targetWidth,
          height: 'max(50dvh, 650px)',
          right: 36,
          borderRadius: '4px 4px 0 0',
          boxShadow: '3.013px 3.013px 24.1px 0 rgba(38, 45, 70, 0.15)',
        };
  }, [dimension, viewport]);

  const launcherVisible = !open && Boolean(launcher);
  const visible = open || launcherVisible;
  const launcherInset = launcher?.inset ?? { top: 0, right: 0 };
  const frameGeometry = launcherVisible
    ? {
        width: (launcher?.width ?? 0) + launcherInset.right,
        height: (launcher?.height ?? 0) + launcherInset.top,
        right: Math.max(0, launcherRight - launcherInset.right),
        border: 0,
        borderRadius: undefined,
        background: 'transparent',
        boxShadow: undefined,
        filter: 'drop-shadow(3.013px 3.013px 12.05px rgba(38, 45, 70, 0.15))',
      }
    : {
        width: geometry.width,
        height: geometry.height,
        right: geometry.right,
        border: geometry.fullscreen ? 0 : '1px solid #d9dfe7',
        borderRadius: geometry.borderRadius,
        background: 'white',
        boxShadow: geometry.boxShadow,
        filter: undefined,
      };

  return (
    <iframe
      ref={setIframeRef}
      id={iframeId}
      src={embedPath}
      title="Nova Assist chat"
      data-testid="nova-embed-iframe"
      allow="clipboard-write; microphone"
      onLoad={onLoad}
      tabIndex={launcherVisible && transparentLauncher ? -1 : undefined}
      style={{
        position: 'fixed',
        top: open && geometry.fullscreen ? 0 : undefined,
        left: open && geometry.fullscreen ? 0 : undefined,
        right: frameGeometry.right,
        bottom: 0,
        zIndex: 1000,
        display: visible ? 'block' : 'none',
        width: frameGeometry.width,
        height: frameGeometry.height,
        border: frameGeometry.border,
        borderRadius: frameGeometry.borderRadius,
        background: frameGeometry.background,
        boxShadow: frameGeometry.boxShadow,
        filter: frameGeometry.filter,
        opacity: launcherVisible && transparentLauncher ? 0 : 1,
        pointerEvents: launcherVisible && transparentLauncher ? 'none' : undefined,
      }}
    />
  );
}
